// src/components/audio/RestrictedAudioPlayer.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX, Play, Pause } from 'lucide-react';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';

// `initialPlaysCompleted` / `initialTime` resume a listening section after a page
// reload (from the exam snapshot), so a reload neither restarts the recording
// nor hands out extra plays. `onProgress` reports the same two values back,
// about once per second while playing. `onDuration` reports the recording's length
// once it is known.
const RestrictedAudioPlayer = ({
  audioUrl,
  autoPlay = true,
  initialVolume = 0.7,
  onAudioEnd = null,
  onProgress = null,
  onDuration = null,
  playCount = 1,
  initialPlaysCompleted = 0,
  initialTime = 0,
  className = ""
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(initialVolume);
  const [isMuted, setIsMuted] = useState(false);
  const [currentPlayCount, setCurrentPlayCount] = useState(initialPlaysCompleted);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Autoplay was blocked by the browser: show the play button, not an error.
  const [needsClick, setNeedsClick] = useState(false);
  const resumeAtRef = useRef(initialTime > 0 ? initialTime : 0);
  const autoPlayTriedRef = useRef(false);
  const lastReportedSecondRef = useRef(-1);
  const playsLeft = currentPlayCount < playCount;
  const isResuming = initialPlaysCompleted > 0 || initialTime > 0;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const reportDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) onDuration?.(audio.duration);
    };
    const seekToResumePoint = () => {
      if (resumeAtRef.current > 0 && audio.duration && resumeAtRef.current < audio.duration) {
        audio.currentTime = resumeAtRef.current;
      }
      resumeAtRef.current = 0;
    };
    const handleTimeUpdate = () => {
      const second = Math.floor(audio.currentTime);
      if (second === lastReportedSecondRef.current) return;
      lastReportedSecondRef.current = second;
      onProgress?.({ playsCompleted: currentPlayCount, time: audio.currentTime });
    };
    const handleError = (e) => {
      setError('Audio konnte nicht geladen werden');
      setIsLoading(false);
      console.error('Audio loading error:', e);
    };

    const handlePlay = () => { setIsPlaying(true); setNeedsClick(false); };
    const handlePause = () => setIsPlaying(false);

    const handleEnded = () => {
      setIsPlaying(false);
      const newPlayCount = currentPlayCount + 1;
      setCurrentPlayCount(newPlayCount);
      onProgress?.({ playsCompleted: newPlayCount, time: 0 });

      // Auto-replay if we haven't reached the play limit
      if (newPlayCount < playCount) {
        setTimeout(() => {
          audio.currentTime = 0;
          audio.play().catch(console.error);
        }, 1000); // 1 second pause between plays
      } else if (onAudioEnd) {
        onAudioEnd();
      }
    };

    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', reportDuration);
    if (audio.readyState >= 1) reportDuration();
    if (resumeAtRef.current > 0) {
      if (audio.readyState >= 1) seekToResumePoint();
      else audio.addEventListener('loadedmetadata', seekToResumePoint, { once: true });
    }

    // Set initial volume
    audio.volume = volume;

    // Auto-play once per mount, only while plays are left. After a reload the
    // browser usually blocks this (no click yet), and the user resumes by hand.
    if (autoPlay && !autoPlayTriedRef.current && currentPlayCount < playCount) {
      autoPlayTriedRef.current = true;
      audio.play().catch((e) => {
        console.log('Auto-play failed (browser policy):', e);
        setNeedsClick(true);
      });
    }

    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', reportDuration);
      audio.removeEventListener('loadedmetadata', seekToResumePoint);
    };
  }, [audioUrl, autoPlay, volume, currentPlayCount, playCount, onAudioEnd, onProgress, onDuration]);

  // Update audio volume when volume state changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0) {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleManualPlay = () => {
    if (audioRef.current && !isPlaying && playsLeft) {
      audioRef.current.play().catch(console.error);
    }
  };

  if (!audioUrl) {
    return (
      <div className={`p-4 bg-gray-100 rounded-lg ${className}`}>
        <p className="text-gray-500 text-sm">Keine Audio-Datei verfügbar</p>
      </div>
    );
  }

  // The <audio> element stays mounted in every state: its listeners are attached
  // once per URL, and unmounting it (as the old error view did) left "Erneut
  // versuchen" with nothing to play.
  const audioElement = (
    <audio
      ref={audioRef}
      src={audioUrl}
      preload="auto"
      style={{ display: 'none' }}
    />
  );

  if (error) {
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        {audioElement}
        <p className="text-red-600 text-sm">{error}</p>
        <button
          onClick={() => { setError(null); audioRef.current?.load(); }}
          className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className={`p-4 bg-blue-50 border border-blue-200 rounded-lg ${className}`}>
      {audioElement}

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Playing indicator */}
          <div className="flex items-center space-x-2">
            {isLoading ? (
              <LogamiLogoSpinner size="lg" decorative={false} alt="Logami loading" />
            ) : (
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                isPlaying ? 'bg-green-500' : 'bg-gray-400'
              }`}>
                {isPlaying ? (
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                ) : (
                  <Play className="w-3 h-3 text-white" />
                )}
              </div>
            )}
            <span className="text-sm font-medium text-gray-700">
              {isLoading ? 'Lädt...' : isPlaying ? 'Spielt ab' : 'Bereit'}
            </span>
          </div>

          {/* Play count indicator */}
          {playCount > 1 && (
            <div className="text-xs text-gray-600 bg-gray-200 px-2 py-1 rounded">
              {currentPlayCount + (isPlaying ? 1 : 0)} / {playCount}
            </div>
          )}
        </div>

        {/* Volume controls */}
        <div className="flex items-center space-x-2">
          <button
            onClick={toggleMute}
            className="p-1 hover:bg-blue-100 rounded"
            title={isMuted ? 'Stumm aufheben' : 'Stumm schalten'}
          >
            {isMuted || volume === 0 ? (
              <VolumeX className="w-4 h-4 text-gray-600" />
            ) : (
              <Volume2 className="w-4 h-4 text-gray-600" />
            )}
          </button>

          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(isMuted ? 0 : volume) * 100}%, #e5e7eb ${(isMuted ? 0 : volume) * 100}%, #e5e7eb 100%)`
            }}
          />

          <span className="text-xs text-gray-500 w-8">
            {Math.round((isMuted ? 0 : volume) * 100)}%
          </span>
        </div>
      </div>

      {/* Manual play button: before the first play, or when autoplay was blocked
          (typically after a page reload, where the audio resumes where it stopped).
          Not gated on isLoading: iOS doesn't preload, so canplay never fires before play(). */}
      {!isPlaying && playsLeft && (needsClick || (currentPlayCount === 0 && !isLoading)) && (
        <button
          onClick={handleManualPlay}
          className="mt-3 w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center space-x-2"
        >
          <Play className="w-4 h-4" />
          <span>{isResuming ? 'Audio fortsetzen' : 'Audio abspielen'}</span>
        </button>
      )}

      {/* Play count info */}
      {(playCount > 1 || !playsLeft) && (
        <p className="text-xs text-gray-500 mt-2">
          {currentPlayCount >= playCount
            ? 'Audio vollständig abgespielt'
            : `Das Audio wird ${playCount} Mal abgespielt`
          }
        </p>
      )}
    </div>
  );
};

export default RestrictedAudioPlayer;